import React from 'react';
import { ProductFilters, ProductCategory } from '../types';
import { useTheme } from '../contexts/ThemeContext';

interface ProductFiltersProps {
  filters: ProductFilters;
  categories: ProductCategory[];
  onFiltersChange: (filters: ProductFilters) => void;
  onClearFilters: () => void;
  onBarcodeSearch?: (barcode: string) => void;
  loading?: boolean;
}

const ProductFiltersComponent: React.FC<ProductFiltersProps> = ({
  filters,
  categories,
  onFiltersChange,
  onClearFilters,
  onBarcodeSearch,
  loading = false
}) => {
  const { theme } = useTheme();

  const handleInputChange = (field: keyof ProductFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value
    });
  };

  const handleBarcodeKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onBarcodeSearch && filters.search) {
      onBarcodeSearch(filters.search);
    }
  };

  const hasActiveFilters = Object.values(filters).some(value => value && value !== 'all');

  return (
    <div className={`p-3 sm:p-4 rounded-lg border space-y-3 sm:space-y-4 ${
      theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
    }`}>
      {/* Search and Barcode */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        <div>
          <label className={`block text-xs sm:text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Search Product
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search products..."
              value={filters.search || ''}
              onChange={(e) => handleInputChange('search', e.target.value)}
              onKeyPress={handleBarcodeKeyPress}
              className={`w-full pl-10 pr-4 py-2.5 sm:py-2 border rounded-md text-sm touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={loading}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          {onBarcodeSearch && (
            <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              Press Enter to search by barcode
            </p>
          )}
        </div>

        <div>
          <label className={`block text-xs sm:text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Category
          </label>
          <select
            value={filters.category || ''}
            onChange={(e) => handleInputChange('category', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 border rounded-md text-sm touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            disabled={loading}
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Status and Sorting */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div>
          <label className={`block text-xs sm:text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Status
          </label>
          <select
            value={filters.status || 'all'}
            onChange={(e) => handleInputChange('status', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 border rounded-md text-sm touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            disabled={loading}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="inactive">Inactive</option>
            <option value="out_of_stock">Out of Stock</option>
          </select>
        </div>

        <div>
          <label className={`block text-xs sm:text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Sort By
          </label>
          <select
            value={filters.sortBy || 'name'}
            onChange={(e) => handleInputChange('sortBy', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 border rounded-md text-sm touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            disabled={loading}
          >
            <option value="name">Name</option>
            <option value="category">Category</option>
            <option value="stock_quantity">Stock Quantity</option>
            <option value="expiry_date">Expiry Date</option>
            <option value="selling_price">Price</option>
            <option value="created_at">Date Added</option>
          </select>
        </div>

        <div>
          <label className={`block text-xs sm:text-sm font-medium mb-2 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Sort Order
          </label>
          <select
            value={filters.sortOrder || 'asc'}
            onChange={(e) => handleInputChange('sortOrder', e.target.value)}
            className={`w-full px-3 py-2.5 sm:py-2 border rounded-md text-sm touch-manipulation focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              theme === 'dark'
                ? 'bg-gray-700 border-gray-600 text-white'
                : 'bg-white border-gray-300 text-gray-900'
            }`}
            disabled={loading}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      {/* Quick Filters */}
      <div className="flex flex-wrap gap-2">
        <span className={`text-xs font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
          Quick Filters:
        </span>
        
        <button
          onClick={() => handleInputChange('status', 'active')}
          className={`px-3 py-1.5 text-xs rounded-full transition-colors touch-manipulation min-h-[32px] ${
            filters.status === 'active'
              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          disabled={loading}
        >
          Active
        </button>

        <button
          onClick={() => handleInputChange('sortBy', 'stock_quantity')}
          className={`px-3 py-1.5 text-xs rounded-full transition-colors touch-manipulation min-h-[32px] ${
            filters.sortBy === 'stock_quantity'
              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
              : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          disabled={loading}
        >
          Low Stock
        </button>

        <button
          onClick={() => handleInputChange('sortBy', 'expiry_date')}
          className={`px-3 py-1.5 text-xs rounded-full transition-colors touch-manipulation min-h-[32px] ${
            filters.sortBy === 'expiry_date'
              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400'
              : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          disabled={loading}
        >
          Expiring Soon
        </button>

        <button
          onClick={() => handleInputChange('status', 'inactive')}
          className={`px-3 py-1.5 text-xs rounded-full transition-colors touch-manipulation min-h-[32px] ${
            filters.status === 'inactive'
              ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              : theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
          disabled={loading}
        >
          Inactive
        </button>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <div className="flex justify-end">
          <button
            onClick={onClearFilters}
            className={`px-4 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors touch-manipulation min-h-[44px] ${
              theme === 'dark'
                ? 'text-gray-300 hover:text-white border border-gray-600 hover:bg-gray-700'
                : 'text-gray-700 hover:text-gray-900 border border-gray-300 hover:bg-gray-50'
            }`}
            disabled={loading}
          >
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductFiltersComponent;